// The last 7 days, as a row that exactly fills the width.
//
// This used to be a horizontal scroller of fixed 56px cells — 440px of content
// in a ~390-430px viewport, so it scrolled by a handful of pixels and then
// handed the gesture to useTabSwipe mid-drag, which read as "the page jumped
// instead of scrolling the days". Cells now flex to fit, so there is no
// overflow and therefore no gesture to lose. Days older than a week are
// reached through the header's date picker rather than by scrolling.
import { useSelectedDate } from "../lib/selectedDate";
import { useT } from "../lib/i18n";
import { hapticSelect } from "../lib/haptics";
import { todayLocal, addDays, unixToLocalDay } from "../lib/date";
import { useMe } from "../lib/useMe";

function fmt(dateStr: string): { dow: number; day: number } {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return { dow: dt.getDay(), day: dt.getDate() };
}

export function DateStrip({ daysBack = 6 }: { daysBack?: number }) {
  const { selectedDate, setSelectedDate } = useSelectedDate();
  const t = useT();
  const { me } = useMe();
  const today = todayLocal();
  // The timeline starts on registration day — a fresh account has no "past
  // days" to browse, so the strip simply grows day by day through week one.
  const regDay = me?.created_at ? unixToLocalDay(me.created_at) : null;
  const dates: string[] = [];
  for (let i = daysBack; i >= 0; i--) {
    const d = addDays(today, -i);
    if (regDay === null || d >= regDay) dates.push(d);
  }

  return (
    <div className="flex gap-1.5">
      {dates.map((d) => {
        const { dow, day } = fmt(d);
        const isSel = d === selectedDate;
        const isToday = d === today;
        return (
          <button
            key={d}
            onClick={() => { hapticSelect(); setSelectedDate(d); }}
            aria-current={isSel ? "date" : undefined}
            className={`flex-1 min-w-0 py-2 rounded-2xl text-center transition-colors ${
              isSel ? "bg-brand text-on-brand press" : "glass text-fg press"
            }`}
          >
            {/* 13px — tab-bar-class ornament, on the type allowlist. */}
            <div className="text-xs uppercase tracking-wide opacity-70">{t(`day.short.${dow}`)}</div>
            <div className="text-lg font-semibold leading-tight tabular-nums">{day}</div>
            {/* Reserved, not conditional — a dot appearing would shift the row. */}
            <div className={`mx-auto mt-1 h-1 w-1 rounded-full bg-current ${isToday ? "opacity-70" : "opacity-0"}`} />
          </button>
        );
      })}
    </div>
  );
}
